import React, { useEffect, useRef, useState } from 'react';
import { Modal } from './ui';

interface EditEmailModalProps {
  isOpen: boolean;
  currentEmail: string;
  onClose: () => void;
  onSubmit: (newEmail: string) => Promise<void>;
}

const TITLE_ID = 'edit-email-title';

export const EditEmailModal: React.FC<EditEmailModalProps> = ({
  isOpen,
  currentEmail,
  onClose,
  onSubmit,
}) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Enter a new email address.');
      inputRef.current?.focus();
      return;
    }
    if (trimmed.toLowerCase() === currentEmail.toLowerCase()) {
      setError('That\'s already your email.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Could not update email.');
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isBusy={submitting} labelledBy={TITLE_ID}>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header
          title="Change email"
          titleId={TITLE_ID}
          subtitle={`Currently signed in as ${currentEmail}.`}
        />

        <Modal.Body>
          <label className="pb-modal-field pt-4">
            <span className="pb-modal-label">New email</span>
            <input
              ref={inputRef}
              type="email"
              className="pb-modal-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={submitting}
            />
          </label>

          {error && <p className="pb-modal-error" role="alert">{error}</p>}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="pb-modal-btn pb-modal-btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="pb-modal-btn pb-modal-btn--primary"
            disabled={submitting || !email.trim()}
          >
            {submitting ? 'Saving…' : 'Save email'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
